import { env } from '@/config/env';
import {
  SapBudgetRecord,
  SapCostCenter,
  SapGlAccount,
  SapGrnRecord,
  SapIntegrationLogItem,
  SapInvoiceRecord,
  SapMaterialMaster,
  SapMetricsSummary,
  SapPaymentRecord,
  SapPoRecord,
  SapPrRecord,
  SapSimulationScenario,
  SapSimulatorConfig,
  SapVendorMaster,
} from '@/types/domain/sap-simulator';
import {
  MOCK_MATERIALS,
  MOCK_VENDORS,
  MOCK_COST_CENTERS,
  MOCK_GL_ACCOUNTS,
  MOCK_PRS,
  MOCK_PURCHASE_ORDERS,
  MOCK_GOODS_RECEIPTS,
  MOCK_INVOICES,
  MOCK_PAYMENTS,
  MOCK_INTEGRATION_LOGS,
} from '@/mocks/database';
import { processQuery, QueryParameters, PaginatedResponse } from './pagination';
import { notifyGlobal } from '@/context/NotificationContext';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const GENERATED_SAP_MATERIALS: SapMaterialMaster[] = MOCK_MATERIALS.map((m) => ({
  id: m.id,
  materialCode: m.code,
  description: m.name,
  category: m.category,
  materialGroup: m.sapMaterialGroup || 'IT-HW',
  uom: m.unitOfMeasure,
  standardPrice: m.standardPrice,
  currency: m.currency,
  plantCode: 'PLANT-1000',
  status: 'ACTIVE',
  createdAt: '2026-01-15T09:00:00Z',
  updatedAt: '2026-08-01T10:00:00Z',
}));

const GENERATED_SAP_VENDORS: SapVendorMaster[] = MOCK_VENDORS.map((v) => ({
  id: v.id,
  vendorCode: v.vendorCode,
  vendorName: v.name,
  category: v.category,
  gstNumber: `GST${v.id}`,
  contactEmail: v.email,
  contactPhone: v.phone,
  paymentTerms: 'Net 30',
  riskRating: 'LOW',
  status: 'ACTIVE',
  createdAt: v.onboardedAt || '2026-01-01T09:00:00Z',
}));

const GENERATED_SAP_COST_CENTERS: SapCostCenter[] = MOCK_COST_CENTERS.map((cc) => ({
  costCenterCode: cc.code,
  description: cc.name,
  departmentName: 'Department',
  plantCode: 'PLANT-1000',
  companyCode: '1000',
  status: cc.isActive ? 'ACTIVE' : 'INACTIVE',
}));

const GENERATED_SAP_GL_ACCOUNTS: SapGlAccount[] = MOCK_GL_ACCOUNTS.map((gl) => ({
  glAccountCode: gl.code,
  description: gl.name,
  accountType: gl.category,
  companyCode: '1000',
  status: gl.isActive ? 'ACTIVE' : 'INACTIVE',
}));

const GENERATED_SAP_BUDGETS: SapBudgetRecord[] = MOCK_COST_CENTERS.map((cc) => ({
  budgetId: `BDG-${cc.code}`,
  companyCode: '1000',
  plantCode: 'PLANT-1000',
  departmentName: 'Department',
  costCenterCode: cc.code,
  allocatedAmount: cc.budgetLimit,
  utilizedAmount: Math.round(cc.budgetUsed * 0.8),
  committedAmount: cc.budgetUsed,
  availableAmount: cc.budgetLimit - cc.budgetUsed,
  status: 'SUFFICIENT',
}));

const GENERATED_SAP_PRS: SapPrRecord[] = MOCK_PRS.map((pr, i) => ({
  id: pr.id,
  sapPrNumber: pr.sapPrNumber || `45000${100 + i}`,
  requestNumber: pr.requestNumber,
  itemNumber: '00010',
  materialCode: 'MAT-9901',
  materialDescription: pr.title,
  quantity: 10,
  uom: 'EA',
  unitPrice: pr.totalValue / 10,
  totalPrice: pr.totalValue,
  currency: pr.currency,
  plantCode: pr.plantCode,
  departmentName: 'Engineering',
  companyCode: '1000',
  purchasingGroup: 'PGRP-001',
  costCenterCode: 'CC-ENG-402',
  glAccountCode: '500100',
  deliveryDate: '2026-10-30',
  status: 'CREATED',
  createdAt: pr.createdAt,
}));

const GENERATED_SAP_POS: SapPoRecord[] = MOCK_PURCHASE_ORDERS.map((po, i) => ({
  id: po.id,
  poNumber: po.poNumber,
  sapPrNumber: `45000${100 + i}`,
  vendorCode: po.vendorId,
  vendorName: po.vendorName,
  poDate: po.createdAt,
  materialCode: 'MAT-9901',
  quantity: 10,
  unitPrice: po.totalAmount / 10,
  totalValue: po.totalAmount,
  currency: po.currency,
  deliveryDate: '2026-10-30',
  deliveredQuantity: 10,
  pendingQuantity: 0,
  status: 'RELEASED',
}));

const GENERATED_SAP_GRNS: SapGrnRecord[] = MOCK_GOODS_RECEIPTS.map((grn) => ({
  id: grn.id,
  grnNumber: grn.grnNumber,
  poNumber: grn.poNumber,
  vendorCode: grn.vendorId,
  materialCode: 'MAT-9901',
  quantityOrdered: 10,
  quantityReceived: 10,
  quantityRejected: 0,
  receiptDate: grn.receivedDate,
  plantCode: 'PLANT-1000',
}));

const GENERATED_SAP_INVOICES: SapInvoiceRecord[] = MOCK_INVOICES.map((inv) => ({
  id: inv.id,
  invoiceNumber: inv.invoiceNumber,
  vendorCode: inv.vendorId,
  poNumber: inv.poNumber,
  invoiceDate: inv.createdAt,
  invoiceAmount: inv.totalAmount,
  matchingStatus: 'MATCHED',
  dueDate: inv.dueDate,
  paymentStatus: 'PAID',
}));

const GENERATED_SAP_PAYMENTS: SapPaymentRecord[] = MOCK_PAYMENTS.map((pay) => ({
  id: pay.id,
  paymentId: pay.paymentNumber,
  invoiceNumber: pay.invoiceNumber,
  vendorCode: pay.vendorId,
  vendorName: pay.vendorName,
  amount: pay.amount,
  currency: pay.currency,
  dueDate: pay.paymentDate,
  paymentDate: pay.paymentDate,
  status: 'PAID',
}));

const GENERATED_SAP_LOGS: SapIntegrationLogItem[] = MOCK_INTEGRATION_LOGS.map((log) => ({
  id: log.id,
  timestamp: log.timestamp,
  apiName: log.operationName || 'BAPI_PO_CREATE',
  direction: 'ProcureAI->SAP',
  transactionType: 'PURCHASE_ORDER',
  requestRef: log.transactionId || log.id,
  sapRef: `450090${log.id}`,
  correlationId: log.transactionId || `CORR-${log.id}`,
  status: log.responseStatus === 'SUCCESS' ? 'SUCCESS' : 'FAILED',
  durationMs: log.durationMs || 240,
  retryCount: 0,
  requestPayloadJson: log.payloadSummary || '{}',
  responsePayloadJson: JSON.stringify({ responseCode: log.responseCode || '200' }),
}));

export class MockSapSimulatorService {
  private config: SapSimulatorConfig = {
    activeScenario: 'SUCCESS',
    simulatedDelayMs: 300,
    environment: 'SIMULATOR_DEVELOPMENT',
    sapIntegrationEnabled: false,
  };

  private materials: SapMaterialMaster[] = [...GENERATED_SAP_MATERIALS];
  private vendors: SapVendorMaster[] = [...GENERATED_SAP_VENDORS];
  private costCenters: SapCostCenter[] = [...GENERATED_SAP_COST_CENTERS];
  private glAccounts: SapGlAccount[] = [...GENERATED_SAP_GL_ACCOUNTS];
  private budgets: SapBudgetRecord[] = [...GENERATED_SAP_BUDGETS];
  private prs: SapPrRecord[] = [...GENERATED_SAP_PRS];
  private pos: SapPoRecord[] = [...GENERATED_SAP_POS];
  private grns: SapGrnRecord[] = [...GENERATED_SAP_GRNS];
  private invoices: SapInvoiceRecord[] = [...GENERATED_SAP_INVOICES];
  private payments: SapPaymentRecord[] = [...GENERATED_SAP_PAYMENTS];
  private logs: SapIntegrationLogItem[] = [...GENERATED_SAP_LOGS];
  private idempotencyMap = new Map<string, SapPrRecord>();

  constructor() {
    this.prs.forEach((pr) => {
      this.idempotencyMap.set(pr.requestNumber, pr);
    });
  }

  getSimulationConfig(): SapSimulatorConfig {
    return { ...this.config };
  }

  setSimulationScenario(scenario: SapSimulationScenario): void {
    this.config.activeScenario = scenario;
    notifyGlobal(
      'SAP Simulation Scenario Updated',
      `Active scenario set to ${scenario}. Development tests will reflect this response mode.`,
      'System'
    );
  }

  async getMetrics(): Promise<SapMetricsSummary> {
    await delay(env.mockDelayMs);
    const totalCalls = this.logs.length;
    const successes = this.logs.filter((l) => l.status === 'SUCCESS').length;
    const fails = this.logs.filter((l) => l.status === 'FAILED' || l.status === 'TIMEOUT').length;
    const retryCount = this.logs.filter((l) => l.status === 'RETRYING' || l.status === 'FAILED').length;

    return {
      totalMaterials: this.materials.length,
      totalVendors: this.vendors.length,
      totalPlants: 4,
      totalCostCenters: this.costCenters.length,
      totalPrs: this.prs.length,
      totalPos: this.pos.length,
      totalGrns: this.grns.length,
      totalInvoices: this.invoices.length,
      totalPayments: this.payments.length,
      totalApiCalls: totalCalls,
      successRatePercentage: totalCalls > 0 ? Math.round((successes / totalCalls) * 100) : 95.3,
      failedIntegrations: fails,
      retryQueueCount: retryCount,
    };
  }

  async getIntegrationLogs(): Promise<SapIntegrationLogItem[]> {
    await delay(env.mockDelayMs);
    return [...this.logs];
  }

  async getMaterials(params?: QueryParameters<SapMaterialMaster>): Promise<PaginatedResponse<SapMaterialMaster>> {
    await delay(env.mockDelayMs);
    return processQuery(this.materials, params);
  }

  async createMaterial(payload: Omit<SapMaterialMaster, 'id' | 'createdAt' | 'updatedAt'>): Promise<SapMaterialMaster> {
    await delay(env.mockDelayMs);
    if (!payload.materialCode || !payload.description || !payload.plantCode) {
      throw new Error('Material Code, Description, and Plant are mandatory.');
    }
    const exists = this.materials.some((m) => m.materialCode.toUpperCase() === payload.materialCode.toUpperCase());
    if (exists) {
      throw new Error(`Material Code ${payload.materialCode} already exists in SAP Material Master.`);
    }

    const newMaterial: SapMaterialMaster = {
      ...payload,
      id: `MAT-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    this.materials.unshift(newMaterial);
    notifyGlobal('SAP Material Master Created', `Created Material ${newMaterial.materialCode} (${newMaterial.description}).`, 'System');
    return newMaterial;
  }

  async getVendors(params?: QueryParameters<SapVendorMaster>): Promise<PaginatedResponse<SapVendorMaster>> {
    await delay(env.mockDelayMs);
    return processQuery(this.vendors, params);
  }

  async createVendor(payload: Omit<SapVendorMaster, 'id' | 'createdAt'>): Promise<SapVendorMaster> {
    await delay(env.mockDelayMs);
    if (!payload.vendorCode || !payload.vendorName || !payload.paymentTerms) {
      throw new Error('Vendor Code, Vendor Name, and Payment Terms are required.');
    }

    const newVendor: SapVendorMaster = {
      ...payload,
      id: `VND-${Date.now()}`,
      createdAt: new Date().toISOString(),
    };

    this.vendors.unshift(newVendor);
    notifyGlobal('SAP Vendor Master Created', `Created Vendor ${newVendor.vendorCode} (${newVendor.vendorName}).`, 'System');
    return newVendor;
  }

  async validateAndCreatePr(payload: {
    requestNumber: string;
    companyCode: string;
    plantCode: string;
    costCenterCode: string;
    glAccountCode: string;
    materialCode: string;
    materialDescription: string;
    quantity: number;
    uom: string;
    unitPrice: number;
    currency: string;
    deliveryDate: string;
  }): Promise<{ success: boolean; pr?: SapPrRecord; correlationId: string; error?: string }> {
    await delay(env.mockDelayMs);
    const correlationId = `CORR-${Date.now()}`;

    if (this.config.activeScenario === 'INVALID_COST_CENTER') {
      return {
        success: false,
        correlationId,
        error: `Cost Center ${payload.costCenterCode} is invalid or inactive in SAP controlling area 1000.`,
      };
    }
    if ((this.config.activeScenario as string) === 'SAP_UNAVAILABLE' || (this.config.activeScenario as string) === 'SIMULATED_FAILURE') {
      const logItem: SapIntegrationLogItem = {
        id: `LOG-${Date.now()}`,
        timestamp: new Date().toISOString(),
        apiName: 'BAPI_PR_CREATE',
        direction: 'ProcureAI->SAP',
        transactionType: 'PR_CREATE',
        requestRef: payload.requestNumber,
        correlationId,
        status: 'FAILED',
        durationMs: 420,
        retryCount: 0,
        requestPayloadJson: JSON.stringify(payload),
        responsePayloadJson: JSON.stringify({ error: 'SAP System Unavailable' }),
      };
      this.logs.unshift(logItem);
      return {
        success: false,
        correlationId,
        error: 'SAP System Unavailable',
      };
    }

    if (this.idempotencyMap.has(payload.requestNumber)) {
      const cached = this.idempotencyMap.get(payload.requestNumber)!;
      return { success: true, pr: cached, correlationId };
    }

    const seq = Math.floor(100 + Math.random() * 900);
    const sapPrNumber = `45000${seq}`;
    const newPr: SapPrRecord = {
      id: `SAP-PR-${seq}`,
      sapPrNumber,
      requestNumber: payload.requestNumber,
      itemNumber: '00010',
      materialCode: payload.materialCode,
      materialDescription: payload.materialDescription,
      quantity: payload.quantity,
      uom: payload.uom,
      unitPrice: payload.unitPrice,
      totalPrice: payload.quantity * payload.unitPrice,
      currency: payload.currency,
      plantCode: payload.plantCode,
      departmentName: 'Engineering',
      companyCode: payload.companyCode,
      purchasingGroup: 'PGRP-001',
      costCenterCode: payload.costCenterCode,
      glAccountCode: payload.glAccountCode,
      deliveryDate: payload.deliveryDate,
      status: 'CREATED',
      createdAt: new Date().toISOString(),
    };

    this.prs.unshift(newPr);
    this.idempotencyMap.set(payload.requestNumber, newPr);

    return { success: true, pr: newPr, correlationId };
  }

  async getPrs(params?: QueryParameters<SapPrRecord>): Promise<PaginatedResponse<SapPrRecord>> {
    await delay(env.mockDelayMs);
    return processQuery(this.prs, params);
  }

  async getPos(params?: QueryParameters<SapPoRecord>): Promise<PaginatedResponse<SapPoRecord>> {
    await delay(env.mockDelayMs);
    return processQuery(this.pos, params);
  }

  async getGrns(params?: QueryParameters<SapGrnRecord>): Promise<PaginatedResponse<SapGrnRecord>> {
    await delay(env.mockDelayMs);
    return processQuery(this.grns, params);
  }

  async getInvoices(params?: QueryParameters<SapInvoiceRecord>): Promise<PaginatedResponse<SapInvoiceRecord>> {
    await delay(env.mockDelayMs);
    return processQuery(this.invoices, params);
  }

  async getPayments(params?: QueryParameters<SapPaymentRecord>): Promise<PaginatedResponse<SapPaymentRecord>> {
    await delay(env.mockDelayMs);
    return processQuery(this.payments, params);
  }

  async getLogs(params?: QueryParameters<SapIntegrationLogItem>): Promise<PaginatedResponse<SapIntegrationLogItem>> {
    await delay(env.mockDelayMs);
    return processQuery(this.logs, params);
  }

  async getCostCenters(): Promise<SapCostCenter[]> {
    await delay(env.mockDelayMs);
    return [...this.costCenters];
  }

  async getGlAccounts(): Promise<SapGlAccount[]> {
    await delay(env.mockDelayMs);
    return [...this.glAccounts];
  }

  async getBudgets(): Promise<SapBudgetRecord[]> {
    await delay(env.mockDelayMs);
    return [...this.budgets];
  }

  async createPurchaseRequisition(payload: {
    prNumber: string;
    requestNumber: string;
    title: string;
    totalAmount: number;
    plantCode: string;
  }): Promise<{ sapPrNumber: string; message: string }> {
    await delay(env.mockDelayMs);
    const sapPrNumber = `10045${Math.floor(100 + Math.random() * 900)}`;
    const newPr: SapPrRecord = {
      id: `SAP-PR-${Date.now()}`,
      sapPrNumber,
      requestNumber: payload.requestNumber,
      itemNumber: '00010',
      materialCode: 'MAT-9901',
      materialDescription: payload.title,
      quantity: 1,
      uom: 'EA',
      unitPrice: payload.totalAmount,
      totalPrice: payload.totalAmount,
      currency: 'USD',
      plantCode: payload.plantCode,
      departmentName: 'Engineering',
      companyCode: '1000',
      purchasingGroup: 'PGRP-001',
      costCenterCode: 'CC-ENG-402',
      glAccountCode: '500100',
      deliveryDate: '2026-10-30',
      status: 'CREATED',
      createdAt: new Date().toISOString(),
    };

    this.prs.unshift(newPr);
    return {
      sapPrNumber,
      message: `Purchase Requisition ${sapPrNumber} created in SAP S/4HANA BAPI.`,
    };
  }

  async retryFailedTransaction(correlationId: string): Promise<SapIntegrationLogItem> {
    await delay(env.mockDelayMs);
    let log = this.logs.find((l) => l.correlationId === correlationId);
    if (!log) {
      log = {
        id: `LOG-RETRY-${Date.now()}`,
        timestamp: new Date().toISOString(),
        apiName: 'BAPI_PR_CREATE',
        direction: 'ProcureAI->SAP',
        transactionType: 'PR_CREATE',
        requestRef: 'REQ-RETRY',
        correlationId,
        status: 'SUCCESS',
        durationMs: 310,
        retryCount: 1,
        requestPayloadJson: '{}',
        responsePayloadJson: '{"status":"SUCCESS"}',
      };
      this.logs.unshift(log);
      return log;
    }
    log.status = 'SUCCESS';
    log.retryCount = (log.retryCount || 0) + 1;
    log.timestamp = new Date().toISOString();
    return log;
  }
}

export const mockSapSimulatorService = new MockSapSimulatorService();
